// Billing shapes and helpers shared by App, PlanPicker, Billing, Dashboard
// and FreePeriodBanner. The frontend doesn't import backend types (see
// frontend/tsconfig.json), so these mirror GET /api/billing/status by hand;
// src/routes/billing.integration.test.ts pins the backend side.

/**
 * The 30-day free period of a store with no plan (review W31). Null when
 * comped or when any subscription row exists. Show days computed from
 * endsAt on the client (daysUntil), never the server's daysLeft, so a
 * cached status can't show a stale count.
 */
export interface FreePeriodView {
  startedAt: string;
  endsAt: string;
  daysLeft?: number;
  ended: boolean;
}

/** Backend-evaluated entitlement; `blocked` is the comp-aware gate. */
export interface EntitlementView {
  entitled: boolean;
  reason: string;
  blocked: boolean;
}

/** The /status fields the free-period logic reads. */
export interface FreePeriodBilling {
  subscription: unknown;
  comped?: boolean;
  entitlement?: { reason: string };
  freePeriod?: FreePeriodView | null;
}

/**
 * How far past endsAt this clock must read before the client calls the
 * period over on its own. A browser clock running fast would otherwise
 * flip a store the server still delivers for to the "delivery has stopped"
 * picker in its last hours (review S13).
 */
export const CLIENT_CLOCK_SKEW_MS = 60 * 60 * 1000;

/**
 * Has this store's free period ended with no plan picked? True on the
 * server's word (freePeriod.ended, or reason free_period_ended) or, for a
 * cached status or a tab left open across the cap, once endsAt plus the
 * skew margin has passed on this clock.
 */
export function freePeriodEnded(billing: FreePeriodBilling, now: number = Date.now()): boolean {
  if (billing.comped || billing.subscription !== null) return false;
  if (billing.entitlement?.reason === "free_period_ended") return true;
  const fp = billing.freePeriod;
  if (fp == null) return false;
  if (fp.ended) return true;
  return Date.parse(fp.endsAt) + CLIENT_CLOCK_SKEW_MS <= now;
}

/**
 * Open Shopify's hosted plan page in the top frame. App Bridge v4
 * intercepts window.open(..., "_top") for the embedded context.
 */
export function openPlanSelection(url: string | undefined): void {
  if (url) window.open(url, "_top");
}
